import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import MedicationIcon from "@mui/icons-material/Medication";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Typography from "@mui/joy/Typography";
import React from "react";
import { AppointmentQuartzClientContext } from "../../../shared/services/api/contexts";
import { Appointment } from "../../../models/appointment";
import { PersonId } from "../../../models/person";
import { unixToDayjs } from "../../../shared/utils/time";
import Chip from "@mui/joy/Chip";

interface ListAppointmentsProps {
  readonly patientId: PersonId;
}

export function ListAppointments({ patientId }: ListAppointmentsProps) {
  const appointmentQuartzClient = React.useContext(AppointmentQuartzClientContext);
  const [appointments, setAppointments] = React.useState<Appointment[]>([]);

  React.useEffect(() => {
    async function fetchAppointments() {
      try {
        const appointments = await appointmentQuartzClient.listAppointments(patientId);
        setAppointments(appointments);
      } catch (error) {
        console.error("Error fetching appointments", error);
      }
    }
    fetchAppointments();
  }, [appointmentQuartzClient, patientId]);

  if (!appointments || appointments.length === 0) {
    return null;
  }

  return (
    <Card variant="soft" color="primary">
      <CardContent>
        <Typography level="title-lg">Appointments</Typography>
        <Box>
          <List>
            {appointments.length === 0 ? (
              <Typography level="body-sm">-</Typography>
            ) : (
              appointments.map((ap) => (
                <ListItem key={ap.appointment_id}>
                  <ListItemButton variant="plain">
                    <ListItemDecorator>
                      {(() => {
                        if (ap.type === "Seance") {
                          return (
                            <MedicationIcon color={ap.status === "booked" ? "success" : "error"} />
                          );
                        }
                        if (ap.status === "booked") {
                          return <EventAvailableIcon color="success" />;
                        }
                        return <EventBusyIcon color="error" />;
                      })()}
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="title-sm">
                        {unixToDayjs(ap.start)?.format("DD MMM YYYY - HH:mm")} -{" "}
                        {unixToDayjs(ap.end)?.format("HH:mm")}
                      </Typography>
                      <Typography level="body-sm">{ap.description}</Typography>
                      <Typography>
                        {ap.identifier.some((id) => id.system?.startsWith("hl7")) && (
                          <Chip size="sm" variant="outlined">
                            Read-only
                          </Chip>
                        )}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </CardContent>
    </Card>
  );
}
